<template>
  <!-- Create or edit an assessment task in one dialog: a rail of steps that can be opened in any order (US 9398).
       Where it is saved, and which parts it has, comes from an adapter (see taskDialogAdapters.js). -->
  <b-modal ref="dialog"
           size="xl"
           modal-class="v2-modal td-modal"
           body-class="p-0"
           hide-header
           hide-footer
           no-close-on-backdrop
           @hidden="$emit('closed')"
  >
    <div class="td" qid="task-dialog" @keydown.meta.enter.prevent="save" @keydown.ctrl.enter.prevent="save">
      <aside class="td-rail">
        <div class="td-rail__eyebrow">{{ task ? $t('task_dialog.edit_eyebrow') : $t('task_dialog.new_eyebrow') }}</div>
        <div class="td-rail__name">{{ railName }}</div>

        <button v-for="(step, index) in steps"
                :key="step.key"
                type="button"
                class="td-step"
                :class="{ 'is-on': current === step.key }"
                :qid="'task-step-' + step.key"
                @click="current = step.key"
        >
          <span class="td-step__dot" :class="'td-step__dot--' + stepState(step)">
            <font-awesome-icon v-if="stepState(step) === 'done'" icon="check"/>
            <template v-else-if="stepState(step) === 'error'">!</template>
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span class="td-step__text">
            <b>{{ $t(stepLabel(step.key) + '.title') }}</b>
            <small :class="{ 'is-warn': stepState(step) === 'error' }">{{ stepSummary(step) }}</small>
          </span>
        </button>

        <div class="td-rail__tip">
          <font-awesome-icon icon="info-circle"/>
          <span>{{ questionnaire ? $t('task_dialog.questionnaire.tip') : $t('task_dialog.tip') }}</span>
        </div>
      </aside>

      <section class="td-main">
        <header class="td-main__head">
          <div class="us-grow">
            <h2>{{ $t(stepLabel(current) + '.heading') }}</h2>
            <p>{{ $t(stepLabel(current) + '.text') }}</p>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('task_dialog.close')" qid="task-dialog-close" @click="close">
            <font-awesome-icon icon="times"/>
          </button>
        </header>

        <div class="td-main__body">
          <b-alert v-if="error" show variant="danger" qid="task-dialog-error">{{ error }}</b-alert>
          <div v-if="readOnly" class="us-note td-lock" qid="task-dialog-read-only">
            <font-awesome-icon icon="lock"/><span>{{ readOnly }}</span>
          </div>
          <div v-if="loading" class="td-loading"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
          <fieldset v-else :disabled="!!readOnly" class="td-fieldset">

          <!-- 1. The task -->
          <div v-if="current === 'question'" class="td-section">
            <b-form-group class="mb-0">
              <label class="us-field-label" for="td-name">{{ $t('task_dialog.fields.name') }}<span class="mandatory"> *</span></label>
              <b-form-input id="td-name"
                            v-model="form.name"
                            :maxLength="255"
                            :state="showErrors && !form.name.trim() ? false : null"
                            :placeholder="$t('maintenance.questions.create.name_placeholder')"
                            qid="task-name"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="mb-0">
              <label class="us-field-label" for="td-reference">{{ $t('task_dialog.fields.reference') }} <span class="us-muted">{{ $t('task_dialog.fields.reference_hint') }}</span></label>
              <b-form-input id="td-reference"
                            v-model="form.reference"
                            class="td-reference"
                            :maxLength="255"
                            :state="referenceTaken ? false : null"
                            :placeholder="$t('maintenance.questions.create.reference_placeholder')"
                            qid="task-reference"
                            @input="checkReference"
              ></b-form-input>
              <b-form-invalid-feedback :state="!referenceTaken">{{ $t('maintenance.questions.create.reference_error') }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group v-if="objectiveRequired" class="mb-0">
              <label class="us-field-label">{{ $t('task_dialog.fields.objective') }}<span class="mandatory"> *</span></label>
              <div class="td-objective">
                <multiselect v-model="objective"
                             :options="objectives"
                             track-by="id"
                             label="name"
                             :allow-empty="false"
                             :class="{ 'is-invalid': showErrors && !objective }"
                             :placeholder="$t('maintenance.control_objective.title_placeholder')"
                             :select-label="$t('system.dropdown_select_label')"
                             :selected-label="$t('system.dropdown_selected_label')"
                             :deselect-label="$t('system.dropdown_deselect_label')"
                             qid="task-objective"
                ></multiselect>
                <b-button v-if="canCreateObjective" variant="secondary" qid="task-objective-new" @click="$emit('create-objective')">
                  <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('template_view.objective.add') }}
                </b-button>
              </div>
            </b-form-group>
          </div>

          <!-- 2. What the auditor does -->
          <div v-else-if="current === 'task'" class="td-section">
            <div class="td-block">
              <div class="td-head">
                <h4>{{ $t('task_dialog.description.title') }}</h4>
                <span v-if="!questionnaire" class="us-muted">{{ $t('task_dialog.description.hint') }}</span>
              </div>
              <!-- Contextual questions have no period audit, so only the point-in-time description -->
              <div v-if="!questionnaire" class="td-tabs" role="tablist">
                <button v-for="part in descriptionParts"
                        :key="part.key"
                        type="button"
                        role="tab"
                        :class="{ 'is-on': descriptionPart === part.key }"
                        :qid="'task-description-' + part.key"
                        @click="descriptionPart = part.key"
                >
                  <i :class="{ 'is-filled': filled(form[part.field]) }"></i>{{ $t('task_dialog.description.' + part.key) }}
                </button>
              </div>
              <template v-for="part in descriptionParts">
                <div v-if="descriptionPart === part.key" :key="part.key" :class="{ 'td-invalid': part.required && showErrors && !filled(form[part.field]) }">
                  <RichTextEditor v-model="form[part.field]"
                                  :headings="false"
                                  compact
                                  :rows="5"
                                  :placeholder="$t('maintenance.questions.create.description_placeholder')"
                                  :qid="'task-' + part.field"
                  ></RichTextEditor>
                  <TaskAttachments :state="attachments" :collection="part.collection"></TaskAttachments>
                </div>
              </template>
            </div>

            <TaskResponseEditor v-model="response"
                                class="td-block"
                                :title="$t('task_dialog.response.title')"
                                :allow-choices="['No', 'Optional', 'Mandatory']"
                                :defaults="defaults.response"
                                :default-options="adapter.features.defaultOptions"
                                :presets="['effective', 'yes_no', 'impact', 'maturity']"
                                qid="task-response"
            ></TaskResponseEditor>

            <div class="td-block">
              <div class="td-head"><h4>{{ questionnaire ? $t('task_dialog.questionnaire.fields_title') : $t('task_dialog.fields_title') }}</h4></div>
              <div class="td-fields" :class="{ 'td-fields--texts': adapter.features.defaultTexts }">
                <span class="td-fields__th">{{ $t('task_dialog.field') }}</span>
                <span class="td-fields__th">{{ $t('task_dialog.shown') }}</span>
                <span v-if="adapter.features.defaultTexts" class="td-fields__th">{{ questionnaire ? $t('task_dialog.questionnaire.default_text') : $t('task_dialog.default_text') }}</span>
                <template v-for="field in shownFields">
                  <span :key="field.allow + '-name'" class="td-fields__name">
                    {{ $t('task_dialog.fields.' + field.key) }}
                    <small>{{ $t('task_dialog.fields.' + field.key + '_hint') }}</small>
                  </span>
                  <TaskChoice :key="field.allow + '-choice'"
                              v-model="form[field.allow]"
                              :choices="['No', 'Optional', 'Mandatory']"
                              :qid="'task-' + field.allow"
                  ></TaskChoice>
                  <div v-if="adapter.features.defaultTexts" :key="field.allow + '-text'">
                    <RichTextEditor v-if="field.text && form[field.allow] !== 'No'"
                                    v-model="form[field.text]"
                                    :headings="false"
                                    compact
                                    :rows="2"
                                    :placeholder="$t('task_dialog.default_text_placeholder')"
                                    :qid="'task-' + field.text"
                    ></RichTextEditor>
                    <span v-else class="td-fields__none">{{ field.text ? $t('task_dialog.field_off') : $t('task_dialog.no_default_text') }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- 3. Documentation from the client -->
          <div v-else-if="current === 'documentation'" class="td-section">
            <div v-if="documentationLocked && !readOnly" class="us-note td-lock" qid="task-dialog-documentation-locked">
              <font-awesome-icon icon="lock"/><span>{{ documentationLocked }}</span>
            </div>
            <fieldset :disabled="!!documentationLocked" class="td-fieldset td-section">
            <div class="td-switch">
              <span class="us-grow">
                <b>{{ $t('task_dialog.documentation.request') }}</b>
                <small>{{ $t('task_dialog.documentation.request_hint') }}</small>
              </span>
              <button type="button"
                      class="us-toggle us-toggle--teal"
                      :class="{ 'is-on': form.data_collection_on }"
                      role="switch"
                      :aria-checked="form.data_collection_on ? 'true' : 'false'"
                      :aria-label="$t('task_dialog.documentation.request')"
                      qid="task-data-collection"
                      @click="form.data_collection_on = !form.data_collection_on"
              ></button>
            </div>

            <template v-if="form.data_collection_on">
              <div class="td-block">
                <div class="td-head">
                  <h4>{{ $t('task_dialog.documentation.what') }}</h4>
                  <span class="us-muted">{{ $t('task_dialog.documentation.what_hint') }}</span>
                </div>
                <div class="td-tabs" role="tablist">
                  <button v-for="part in documentationParts"
                          :key="part.key"
                          type="button"
                          role="tab"
                          :class="{ 'is-on': documentationPart === part.key }"
                          :qid="'task-documentation-' + part.key"
                          @click="documentationPart = part.key"
                  >
                    <i :class="{ 'is-filled': !!form[part.field].trim() }"></i>{{ $t('task_dialog.description.' + part.key) }}
                  </button>
                </div>
                <template v-for="part in documentationParts">
                  <div v-if="documentationPart === part.key" :key="part.key">
                    <b-form-textarea v-model="form[part.field]"
                                     rows="3"
                                     max-rows="10"
                                     :state="part.required && showErrors && !form[part.field].trim() ? false : null"
                                     :placeholder="$t('maintenance.questions.create.data_collection_description_placeholder')"
                                     :qid="'task-' + part.field"
                    ></b-form-textarea>
                    <TaskAttachments :state="attachments" :collection="part.collection"></TaskAttachments>
                  </div>
                </template>
              </div>

              <div class="td-block">
                <div class="td-head"><h4>{{ $t('task_dialog.documentation.client') }}</h4></div>
                <div class="td-fields">
                  <span class="td-fields__name">{{ $t('task_dialog.documentation.comment') }}</span>
                  <TaskChoice v-model="form.data_collection_add_description" :choices="['Optional', 'Mandatory']" qid="task-data-collection-comment"></TaskChoice>
                  <span class="td-fields__name">{{ $t('task_dialog.documentation.attachments') }}</span>
                  <TaskChoice v-model="form.data_collection_add_attachments" :choices="['No', 'Optional', 'Mandatory']" qid="task-data-collection-attachments"></TaskChoice>
                </div>
              </div>

              <TaskResponseEditor v-model="responseDoc"
                                  class="td-block"
                                  :title="$t('task_dialog.documentation.conclusion')"
                                  :allow-choices="['No', 'Optional', 'Mandatory', 'Auditor']"
                                  :defaults="defaults.documentation"
                                  :default-options="adapter.features.defaultOptions"
                                  :presets="['received', 'yes_no']"
                                  qid="task-response-doc"
              ></TaskResponseEditor>
            </template>
            </fieldset>
          </div>

          <!-- 4. Controls -->
          <div v-else-if="current === 'controls'" class="td-section">
            <div class="td-block">
              <div class="td-head">
                <h4>{{ $t('task_dialog.controls.framework') }}</h4>
                <span class="us-muted">{{ $t('task_dialog.controls.framework_hint') }}</span>
              </div>
              <div class="td-chips">
                <span v-for="(control, index) in form.framework_controls" :key="control.id" class="td-chip">
                  <b v-if="control.reference">{{ control.reference }}</b>{{ control.name }}
                  <button type="button" :aria-label="$t('task_dialog.attachments.remove')" qid="task-framework-control-remove" @click="form.framework_controls.splice(index, 1)"><font-awesome-icon icon="times"/></button>
                </span>
                <span v-if="!form.framework_controls.length" class="us-muted">{{ $t('task_dialog.controls.none') }}</span>
              </div>
              <div class="td-picker">
                <multiselect v-model="framework"
                             :options="frameworks"
                             track-by="id"
                             label="name"
                             :placeholder="$t('task_dialog.controls.choose_framework')"
                             :select-label="$t('system.dropdown_select_label')"
                             :selected-label="$t('system.dropdown_selected_label')"
                             :deselect-label="$t('system.dropdown_deselect_label')"
                             qid="task-framework"
                             @input="loadFrameworkControls"
                ></multiselect>
                <multiselect v-model="frameworkControl"
                             :options="frameworkControlOptions"
                             track-by="id"
                             label="name"
                             :disabled="!framework"
                             :placeholder="$t('task_dialog.controls.choose_control')"
                             :select-label="$t('system.dropdown_select_label')"
                             :selected-label="$t('system.dropdown_selected_label')"
                             :deselect-label="$t('system.dropdown_deselect_label')"
                             qid="task-framework-control"
                ></multiselect>
                <b-button variant="secondary" :disabled="!frameworkControl" qid="task-framework-control-add" @click="addFrameworkControl">
                  <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('task_dialog.attachments.add') }}
                </b-button>
              </div>
            </div>

            <div v-if="adapter.features.linkedControls" class="td-block">
              <div class="td-head">
                <h4>{{ $t('task_dialog.controls.linked') }}</h4>
                <span class="us-muted">{{ $t('task_dialog.controls.linked_hint') }}</span>
              </div>
              <div class="td-chips">
                <span v-for="(control, index) in form.linked_controls" :key="control.id" class="td-chip">
                  <b v-if="control.reference">{{ control.reference }}</b>{{ control.name }}
                  <button type="button" :aria-label="$t('task_dialog.attachments.remove')" qid="task-linked-control-remove" @click="form.linked_controls.splice(index, 1)"><font-awesome-icon icon="times"/></button>
                </span>
                <span v-if="!form.linked_controls.length" class="us-muted">{{ $t('task_dialog.controls.none') }}</span>
              </div>
              <div class="td-picker">
                <multiselect v-model="linkedTemplate"
                             :options="templates"
                             track-by="id"
                             label="name"
                             :placeholder="$t('task_dialog.controls.choose_template')"
                             :select-label="$t('system.dropdown_select_label')"
                             :selected-label="$t('system.dropdown_selected_label')"
                             :deselect-label="$t('system.dropdown_deselect_label')"
                             qid="task-linked-template"
                             @input="loadLinkedTasks"
                ></multiselect>
                <multiselect v-model="linkedTask"
                             :options="linkedTaskOptions"
                             track-by="id"
                             :custom-label="taskLabel"
                             :disabled="!linkedTemplate"
                             :placeholder="$t('task_dialog.controls.choose_task')"
                             :select-label="$t('system.dropdown_select_label')"
                             :selected-label="$t('system.dropdown_selected_label')"
                             :deselect-label="$t('system.dropdown_deselect_label')"
                             qid="task-linked-task"
                ></multiselect>
                <b-button variant="secondary" :disabled="!linkedTask" qid="task-linked-control-add" @click="addLinkedControl">
                  <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('task_dialog.attachments.add') }}
                </b-button>
              </div>
            </div>
          </div>

          </fieldset>
        </div>

        <footer class="td-main__foot">
          <span class="us-grow td-keys"><template v-if="!readOnly"><span class="home-kbd">⌘ ↵</span> {{ $t('task_dialog.save_hint') }}</template></span>
          <b-button v-if="stepIndex > 0" variant="secondary" qid="task-dialog-back" @click="go(-1)">
            <font-awesome-icon icon="chevron-left" class="mr-1"/>{{ $t('task_dialog.back') }}
          </b-button>
          <b-button v-if="!readOnly" :variant="isLast || task ? 'success' : 'secondary'" :disabled="saving || loading" qid="task-dialog-save" @click="save">
            <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>{{ task ? $t('task_dialog.save_changes') : $t('task_dialog.save') }}
          </b-button>
          <b-button v-if="!isLast" :variant="task ? 'secondary' : 'success'" qid="task-dialog-next" @click="go(1)">
            {{ $t('task_dialog.next', { step: $t(stepLabel(steps[stepIndex + 1].key) + '.title') }) }}<font-awesome-icon icon="chevron-right" class="ml-1"/>
          </b-button>
        </footer>
      </section>
    </div>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import RichTextEditor from '@/components/richText/RichTextEditor.vue';
  import TaskChoice from '@/components/assessment/TaskChoice.vue';
  import TaskResponseEditor from '@/components/assessment/TaskResponseEditor.vue';
  import TaskAttachments from '@/components/assessment/TaskAttachments.vue';
  import { richTextToPlainText } from '@/components/richText/richText.js';
  import { makeOption, parseOptions } from '@/util/dropdownOptions.js';

  // Collections of a task's files and links, and the keys a new task sends its files under
  const COLLECTIONS = {
    question: 'files',
    question_two: 'files_two',
    data_collection: 'data_collection_files',
    data_collection_two: 'data_collection_two_files'
  };

  function emptyResponse() {
    return { allow: 'Optional', type: null, options: [], use_default: false, yes_no: yesNo() };
  }

  function yesNo(options) {
    let saved = parseOptions(options);
    let value = (index, fallback) => saved[index] && saved[index].value && saved[index].value.value ? saved[index].value.value : fallback;
    return [
      Object.assign(makeOption('Yes', value(0, 'Positive')), saved[0] && saved[0].color ? { color: saved[0].color, icon: saved[0].icon } : {}),
      Object.assign(makeOption('No', value(1, 'Negative')), saved[1] && saved[1].color ? { color: saved[1].color, icon: saved[1].icon } : {})
    ];
  }

  // A saved task's response as the dialog edits it
  function responseOf(allow, type, options, useDefault) {
    return {
      allow: allow || 'Optional',
      type: type || null,
      options: type === 'Dropdown' ? parseOptions(options) : [],
      use_default: !!useDefault,
      yes_no: yesNo(type === 'Yes/No' ? options : null)
    };
  }

  export default {
    name: 'TaskDialog',
    components: {
      RichTextEditor,
      TaskChoice,
      TaskResponseEditor,
      TaskAttachments
    },
    props: {
      adapter: {
        type: Object,
        required: true
      },
      // Control objectives to put the task in; none means the task isn't in one
      objectives: {
        type: Array,
        default: () => []
      },
      objectiveRequired: {
        type: Boolean,
        default: false
      },
      canCreateObjective: {
        type: Boolean,
        default: false
      },
      // Why nothing can be changed (e.g. a completed task or a locked project); empty when it can
      readOnly: {
        type: String,
        default: ''
      },
      // Why the documentation collection can't be changed (e.g. during operating effectiveness testing)
      documentationLocked: {
        type: String,
        default: ''
      },
      // The template's default drop-downs
      defaults: {
        type: Object,
        default: () => ({ response: [], documentation: [] })
      }
    },
    data() {
      return {
        task: null,
        current: 'question',
        form: this.emptyForm(),
        response: emptyResponse(),
        responseDoc: emptyResponse(),
        objective: null,
        attachments: this.emptyAttachments(),
        descriptionPart: 'one',
        documentationPart: 'one',
        descriptionParts: [
          { key: 'one', field: 'description', collection: 'question', required: true },
          { key: 'two', field: 'description_two', collection: 'question_two' }
        ],
        documentationParts: [
          { key: 'one', field: 'data_collection_description', collection: 'data_collection', required: true },
          { key: 'two', field: 'data_collection_description_two', collection: 'data_collection_two' }
        ],
        auditorFields: [
          { key: 'control_description', allow: 'add_control_description', text: 'control_description_default_text' },
          { key: 'performed_test', allow: 'add_performed_text', text: 'performed_test_default_text' },
          { key: 'conclusion', allow: 'add_description', text: 'conclusion_default_text' },
          { key: 'testing_notes', allow: 'add_testing_notes', text: 'testing_notes_default_text' },
          { key: 'notes', allow: 'add_notes' },
          { key: 'attachments', allow: 'add_attachments' }
        ],
        questionnaireFields: [
          { key: 'comment', allow: 'add_description', text: 'conclusion_default_text' },
          { key: 'respondent_attachments', allow: 'add_attachments' }
        ],
        frameworks: [],
        framework: null,
        frameworkControlOptions: [],
        frameworkControl: null,
        templates: [],
        linkedTemplate: null,
        linkedTaskOptions: [],
        linkedTask: null,
        referenceTaken: false,
        showErrors: false,
        error: '',
        loading: false,
        saving: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      // A contextual (risk) question has one respondent and no documentation collection or controls (US 9480)
      questionnaire() {
        return !!this.adapter.features.questionnaire;
      },
      steps() {
        if (this.questionnaire) {
          return [
            { key: 'question', required: true },
            { key: 'task', required: true }
          ];
        }
        return [
          { key: 'question', required: true },
          { key: 'task', required: true },
          { key: 'documentation', optional: true },
          { key: 'controls', optional: true }
        ];
      },
      shownFields() {
        return this.questionnaire ? this.questionnaireFields : this.auditorFields;
      },
      stepIndex() {
        return _.findIndex(this.steps, { key: this.current });
      },
      isLast() {
        return this.stepIndex === this.steps.length - 1;
      },
      railName() {
        let name = [this.form.reference, this.form.name].filter(part => part && part.trim()).join(' · ');
        return name || (this.objective ? this.objective.name : this.$t('task_dialog.untitled'));
      },
      // What is missing, per step
      problems() {
        let problems = { question: [], task: [], documentation: [], controls: [] };

        if (!this.form.name.trim()) {
          problems.question.push(this.$t('task_dialog.errors.name'));
        }
        if (this.referenceTaken) {
          problems.question.push(this.$t('maintenance.questions.create.reference_error'));
        }
        if (this.objectiveRequired && !this.objective) {
          problems.question.push(this.$t('maintenance.questions.create.control_objective_error_message'));
        }
        if (!this.filled(this.form.description)) {
          problems.task.push(this.$t('task_dialog.errors.description'));
        }
        problems.task.push(...this.responseProblems(this.response, this.defaults.response));
        if (this.form.data_collection_on && !this.documentationLocked) {
          if (!this.form.data_collection_description.trim()) {
            problems.documentation.push(this.$t('task_dialog.errors.documentation'));
          }
          problems.documentation.push(...this.responseProblems(this.responseDoc, this.defaults.documentation));
        }
        return problems;
      },
      problemCount() {
        return Object.values(this.problems).reduce((count, list) => count + list.length, 0);
      }
    },
    methods: {
      stepLabel(key) {
        return (this.questionnaire && (key === 'question' || key === 'task') ? 'task_dialog.questionnaire_steps.' : 'task_dialog.steps.') + key;
      },
      emptyForm() {
        // The auditor's fields of a control assessment are off on a contextual question
        let auditor = this.adapter && this.adapter.features.questionnaire ? 'No' : 'Optional';
        return {
          name: '',
          reference: '',
          description: '',
          description_two: '',
          add_control_description: auditor,
          add_performed_text: auditor,
          add_testing_notes: auditor,
          add_description: 'Optional',
          add_notes: auditor,
          add_attachments: 'Optional',
          control_description_default_text: '',
          performed_test_default_text: '',
          testing_notes_default_text: '',
          conclusion_default_text: '',
          data_collection_on: false,
          data_collection_description: '',
          data_collection_description_two: '',
          data_collection_add_description: 'Optional',
          data_collection_add_attachments: 'Optional',
          framework_controls: [],
          linked_controls: []
        };
      },
      emptyAttachments() {
        return {
          media: [],
          savedLinks: [],
          removedMedia: [],
          removedLinks: [],
          files: { question: [], question_two: [], data_collection: [], data_collection_two: [] },
          links: { question: [], question_two: [], data_collection: [], data_collection_two: [] }
        };
      },
      // Opens the dialog for a new task (in an objective, if given) or for a saved one
      open(task, objective) {
        this.task = task || null;
        this.current = 'question';
        this.descriptionPart = 'one';
        this.documentationPart = 'one';
        this.form = this.emptyForm();
        this.attachments = this.emptyAttachments();
        this.response = emptyResponse();
        this.responseDoc = emptyResponse();
        this.objective = objective ? _.find(this.objectives, { id: objective.id }) || objective : null;
        this.framework = null;
        this.frameworkControl = null;
        this.linkedTemplate = null;
        this.linkedTask = null;
        this.referenceTaken = false;
        this.showErrors = false;
        this.error = '';

        if (task) {
          this.loading = true;
          this.adapter.load(task).then(saved => {
            this.task = saved;
            this.fill(saved);
            this.loading = false;
          }, () => {
            this.loading = false;
            this.error = this.$t('users_page.error');
          });
        } else {
          // New tasks start on the template's default drop-downs
          if (this.adapter.features.defaultOptions && this.defaults.response.length) {
            this.response = Object.assign(emptyResponse(), { type: 'Dropdown', use_default: true });
          }
          if (this.adapter.features.defaultOptions && this.defaults.documentation.length) {
            this.responseDoc = Object.assign(emptyResponse(), { type: 'Dropdown', use_default: true });
          }
        }
        this.$refs.dialog.show();
      },
      close() {
        this.$refs.dialog.hide();
      },
      fill(task) {
        let form = this.emptyForm();
        Object.keys(form).forEach(key => {
          if (task[key] !== undefined && task[key] !== null && typeof form[key] === typeof task[key]) {
            form[key] = task[key];
          }
        });
        form.name = task[this.adapter.nameField] || '';
        form.data_collection_on = !!Number(task.data_collection_on);
        form.framework_controls = (task.framework_controls || []).slice();
        form.linked_controls = (task.linked_controls || []).slice();
        this.form = form;

        this.response = responseOf(task.add_response, task.response_type, task.response_type_options, task.use_default_options);
        this.responseDoc = responseOf(task.add_response_doc, task.response_type_doc, task.response_type_options_doc, task.use_default_options_doc);
        this.objective = task.control_objective ? _.find(this.objectives, { id: task.control_objective.id }) || task.control_objective : null;
        this.attachments.media = task.media || [];
        this.attachments.savedLinks = task.links || [];
      },
      // Rich text counts as filled in when it has text, not just markup
      filled(value) {
        return !!richTextToPlainText(value || '').trim();
      },
      setObjective(objective) {
        this.objective = objective;
      },
      responseProblems(response, defaults) {
        if (response.allow === 'No') {
          return [];
        }
        if (!response.type) {
          return [this.$t('task_dialog.errors.type')];
        }
        if (response.type === 'Dropdown' && !(response.use_default && defaults.length) && !response.options.filter(option => option.title.trim()).length) {
          return [this.$t('task_dialog.errors.options')];
        }
        return [];
      },
      stepState(step) {
        if (this.showErrors && this.problems[step.key].length) {
          return 'error';
        }
        if (step.key === this.current) {
          return 'current';
        }
        if (step.optional && !this.stepFilled(step.key)) {
          return 'optional';
        }
        return this.problems[step.key].length ? 'todo' : 'done';
      },
      stepFilled(key) {
        if (key === 'documentation') {
          return this.form.data_collection_on;
        }
        return this.form.framework_controls.length + this.form.linked_controls.length > 0;
      },
      stepSummary(step) {
        if (this.showErrors && this.problems[step.key].length) {
          return this.problems[step.key][0];
        }
        switch (step.key) {
          case 'question':
            return this.objective ? this.objective.name : this.$t('task_dialog.summary.question');
          case 'task':
            return this.response.allow === 'No' ? this.$t('task_dialog.summary.no_response') : (this.response.type ? this.typeLabel(this.response) : this.$t('task_dialog.summary.task'));
          case 'documentation':
            return this.form.data_collection_on ? this.$t('task_dialog.summary.requested') : this.$t('task_dialog.summary.not_requested');
          default: {
            let count = this.form.framework_controls.length + this.form.linked_controls.length;
            return count ? this.$tc('task_dialog.summary.controls', count, { count: count }) : this.$t('task_dialog.summary.optional');
          }
        }
      },
      typeLabel(response) {
        let key = { 'Dropdown': 'dropdown', 'Yes/No': 'yes_no', 'Datepicker': 'date' }[response.type];
        let label = this.$t('task_dialog.types.' + key + '.title');
        if (response.type === 'Dropdown') {
          label += ' · ' + (response.use_default ? this.$t('task_dialog.summary.default') : this.$tc('task_dialog.summary.options', response.options.length, { count: response.options.length }));
        }
        return label;
      },
      taskLabel(task) {
        return [task.reference, task.name || task.title].filter(Boolean).join(' · ');
      },
      go(direction) {
        this.current = this.steps[this.stepIndex + direction].key;
        this.loadStep();
      },
      // Lists for the Controls step load the first time it is opened
      loadStep() {
        if (this.current !== 'controls') {
          return;
        }
        if (!this.frameworks.length) {
          piincHttp.get('frameworks', { params: { client: this.getClient.slug, per_page: 100 } }).then(response => {
            this.frameworks = response.data.items;
          }, () => {});
        }
        if (this.adapter.features.linkedControls && !this.templates.length) {
          piincHttp.get('assessments', { params: { client: this.getClient.slug, per_page: 100, type: 'control' } }).then(response => {
            this.templates = response.data.items;
          }, () => {});
        }
      },
      loadFrameworkControls() {
        this.frameworkControl = null;
        this.frameworkControlOptions = [];
        if (!this.framework) {
          return;
        }
        piincHttp.get('framework-controls', { params: { client: this.getClient.slug, per_page: 500, framework: this.framework.id } }).then(response => {
          let chosen = _.map(this.form.framework_controls, 'id');
          this.frameworkControlOptions = response.data.items.filter(item => chosen.indexOf(item.id) === -1);
        }, () => {});
      },
      addFrameworkControl() {
        this.form.framework_controls.push(this.frameworkControl);
        this.frameworkControlOptions = this.frameworkControlOptions.filter(item => item.id !== this.frameworkControl.id);
        this.frameworkControl = null;
      },
      loadLinkedTasks() {
        this.linkedTask = null;
        this.linkedTaskOptions = [];
        if (!this.linkedTemplate) {
          return;
        }
        piincHttp.get('questions', { params: { client: this.getClient.slug, per_page: 500, assessment: this.linkedTemplate.id } }).then(response => {
          let chosen = _.map(this.form.linked_controls, 'id');
          this.linkedTaskOptions = response.data.items.filter(item => (!this.task || item.id !== this.task.id) && chosen.indexOf(item.id) === -1);
        }, () => {});
      },
      addLinkedControl() {
        this.form.linked_controls.push(this.linkedTask);
        this.linkedTaskOptions = this.linkedTaskOptions.filter(item => item.id !== this.linkedTask.id);
        this.linkedTask = null;
      },
      checkReference: _.debounce(function() {
        let reference = this.form.reference.trim();
        if (!reference) {
          this.referenceTaken = false;
          return;
        }
        this.adapter.duplicateReference(reference, this.task).then(taken => {
          this.referenceTaken = taken;
        }, () => {});
      }, 400),
      // The options a response saves: the default's, the custom list, or Yes and No
      optionsOf(response, defaults) {
        if (response.type === 'Dropdown') {
          let options = response.use_default && defaults.length ? defaults : response.options.filter(option => option.title.trim());
          return JSON.stringify(options);
        }
        if (response.type === 'Yes/No') {
          return JSON.stringify(response.yes_no);
        }
        return null;
      },
      payload() {
        let form = this.form;
        let payload = {
          reference: form.reference.trim(),
          description: form.description,
          description_two: form.description_two,
          add_response: this.response.allow,
          add_control_description: form.add_control_description,
          add_performed_text: form.add_performed_text,
          add_testing_notes: form.add_testing_notes,
          add_description: form.add_description,
          add_notes: form.add_notes,
          add_attachments: form.add_attachments,
          data_collection_on: form.data_collection_on ? 1 : 0,
          framework_controls: _.map(form.framework_controls, 'id')
        };
        payload[this.adapter.nameField] = form.name.trim();

        if (this.response.type) {
          payload.response_type = this.response.type;
          let options = this.optionsOf(this.response, this.defaults.response);
          if (options !== null) {
            payload.response_type_options = options;
          }
        }
        if (this.objective) {
          payload.control_objective_id = this.objective.id;
        }
        // A locked documentation collection is left as it is
        if (this.documentationLocked) {
          delete payload.data_collection_on;
        } else if (form.data_collection_on) {
          Object.assign(payload, {
            data_collection_description: form.data_collection_description,
            data_collection_description_two: form.data_collection_description_two,
            data_collection_add_description: form.data_collection_add_description,
            data_collection_add_attachments: form.data_collection_add_attachments,
            add_response_doc: this.responseDoc.allow
          });
          if (this.responseDoc.type) {
            payload.response_type_doc = this.responseDoc.type;
            let options = this.optionsOf(this.responseDoc, this.defaults.documentation);
            if (options !== null) {
              payload.response_type_options_doc = options;
            }
          }
        }
        if (this.adapter.features.defaultTexts) {
          this.auditorFields.filter(field => field.text).forEach(field => {
            payload[field.text] = form[field.text];
          });
        }
        if (this.adapter.features.defaultOptions) {
          payload.use_default_options = this.response.type === 'Dropdown' && this.response.use_default && this.defaults.response.length ? 1 : 0;
          payload.use_default_options_doc = form.data_collection_on && this.responseDoc.type === 'Dropdown' && this.responseDoc.use_default && this.defaults.documentation.length ? 1 : 0;
        }
        if (this.adapter.features.linkedControls) {
          payload.linked_controls = _.map(form.linked_controls, 'id');
        }
        // A contextual question keeps only its own fields; the ones of a control assessment are turned off
        if (this.questionnaire) {
          Object.assign(payload, {
            add_control_description: 'No',
            add_performed_text: 'No',
            add_testing_notes: 'No',
            add_notes: 'No'
          });
          if (!this.documentationLocked) {
            payload.data_collection_on = 0;
          }
          delete payload.description_two;
        }
        return payload;
      },
      // A new task sends everything, files included, in one form
      formData(payload) {
        let formData = new FormData();
        Object.keys(payload).forEach(key => {
          if (Array.isArray(payload[key])) {
            payload[key].forEach(value => formData.append(key + '[]', value));
          } else {
            formData.append(key, payload[key] === null ? '' : payload[key]);
          }
        });
        Object.keys(COLLECTIONS).forEach(collection => {
          this.attachments.files[collection].forEach(file => formData.append(COLLECTIONS[collection] + '[]', file));
        });
        return formData;
      },
      // Files and links of a saved task change through the documents and links endpoints
      saveAttachments(task, uploadFiles) {
        let requests = [];
        let modelType = this.adapter.modelType(task);

        Object.keys(COLLECTIONS).forEach(collection => {
          if (this.documentationLocked && collection.indexOf('data_collection') === 0) {
            return;
          }
          if (uploadFiles && this.attachments.files[collection].length) {
            let formData = new FormData();
            this.attachments.files[collection].forEach(file => formData.append('document[]', file));
            formData.append('name', 'documents');
            formData.append('collection', collection);
            formData.append('model_type', modelType);
            formData.append('model_id', task.id);
            requests.push(piincHttp.post('documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
          }
          this.attachments.links[collection].forEach(link => {
            requests.push(piincHttp.post('links', { url: link.url, name: link.name, collection: collection, model_type: modelType, model_id: task.id }));
          });
        });
        this.attachments.removedMedia.forEach(id => requests.push(piincHttp.delete('documents/' + id)));
        this.attachments.removedLinks.forEach(id => requests.push(piincHttp.delete('links/' + id)));
        return Promise.all(requests);
      },
      save() {
        if (this.saving || this.loading || this.readOnly) {
          return;
        }
        this.showErrors = true;
        if (this.problemCount) {
          this.current = _.find(this.steps, step => this.problems[step.key].length).key;
          this.error = this.$t('task_dialog.errors.summary');
          return;
        }

        let payload = this.payload();
        let isNew = !this.task;
        this.error = '';
        this.saving = true;

        let request = isNew ? this.adapter.create(this.formData(payload)) : this.adapter.update(this.task, payload);
        request
          .then(task => this.saveAttachments(task, !isNew).then(() => task))
          .then(task => {
            this.saving = false;
            this.$toastr('success', isNew ? this.$t('maintenance.questions.create.success') : this.$t('maintenance.questions.update.success'));
            this.$emit('saved', task);
            this.close();
          }, error => {
            this.saving = false;
            this.error = error && error.data && error.data.message ? error.data.message : this.$t('users_page.error');
          });
      }
    },
    watch: {
      current() {
        this.loadStep();
      }
    }
  }
</script>
